// Detalle que se muestra al expandir un usuario en DailySettlementControlTab.
// Consulta sus cobros del día seleccionado y muestra recibos, montos, horas y tiempos de liquidación.
// El recibo enlaza al cobro y los métodos de pago se consultan en un tooltip.
import React, { useEffect, useRef, useState } from 'react'
import { WarningCircle } from '@phosphor-icons/react'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import { UI_COLOR_ERROR_MATERIAL, UI_COLOR_ERROR_MATERIAL_BG } from '../../constants/uiColors'
import DetailDataTable from '../../components/table/DetailDataTable'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import { Link as RouterLink } from 'react-router-dom'
import AppChip from '../../components/chips/AppChip'
import { dailyControlRequest } from './dailyControlApi'
import { formatMoney } from '../../helpers/utils'
import { elapsedLabel, operationalTime } from './helpers/dailyControlHelpers'
import {
  DAILY_CHARGES_COLUMNS,
  DAILY_CHARGES_CONFIG,
  DAILY_CHARGES_TEXT
} from './constants/dailySettlementChargesConstants'

export default function DailySettlementCharges({ date, userUid, cache }) {
  const localCache = useRef(new Map())
  const chargesCache = cache || localCache.current
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [errorAlertOpen, setErrorAlertOpen] = useState(true)
  useEffect(() => {
    let active = true
    setLoading(true)
    setData(null)
    setError('')
    setErrorAlertOpen(true)
    const key = JSON.stringify([date, userUid])
    // Conserva también la petición en curso al cerrar el detalle para no repetirla al abrirlo.
    if (!chargesCache.has(key)) {
      chargesCache.set(
        key,
        dailyControlRequest({ date, user_uid: userUid }, { endpoint: '/charges' })
      )
    }
    chargesCache
      .get(key)
      .then((result) => {
        if (active) setData(result)
      })
      .catch((reason) => {
        if (active) setError(reason.message)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [date, userUid, chargesCache])
  if (error)
    return (
      <UbiquaTowerToastAlert
        open={errorAlertOpen}
        severity="error"
        title="Error al cargar el control diario"
        message={error}
        icon={<WarningCircle size={18} aria-hidden="true" />}
        colors={{ iconBackground: UI_COLOR_ERROR_MATERIAL_BG, progress: UI_COLOR_ERROR_MATERIAL }}
        onClose={() => setErrorAlertOpen(false)}
      />
    )
  const user = data?.user
  // Prioriza los pendientes y conserva el orden cronológico dentro de cada grupo.
  const rows = [...(data?.result || [])].sort((left, right) => {
    const statusOrder = Number(Boolean(left.settled_at)) - Number(Boolean(right.settled_at))
    return statusOrder || new Date(left.charged_at).getTime() - new Date(right.charged_at).getTime()
  })
  const columnRenderers = {
    charge_uid: (row) => (
      <Link
        component={RouterLink}
        to={`/cobros/${encodeURIComponent(row.charge_uid)}`}
        className="daily-control-receipt-code ubiqua-code"
        title={row.charge_uid}
        underline="always"
        sx={{ fontSize: 'inherit' }}
      >
        {row.charge_uid}
      </Link>
    ),
    payment_method: (row) => {
      const methods = [...new Set(row.payments?.map((payment) => payment.method).filter(Boolean))]
      if (methods.length > 1) {
        return (
          <Tooltip title={methods.join(', ')} placement="top" arrow describeChild>
            <span tabIndex={0}>{DAILY_CHARGES_TEXT.MULTIPLE_METHODS(methods.length)}</span>
          </Tooltip>
        )
      }
      return methods[0] || row.payment_method || DAILY_CHARGES_TEXT.MISSING_VALUE
    },
    charged_at: (row) => operationalTime(row.charged_at, DAILY_CHARGES_CONFIG.TIME_FORMAT),
    settled_at: (row) =>
      row.settled_at ? (
        operationalTime(row.settled_at, DAILY_CHARGES_CONFIG.SETTLED_AT_FORMAT)
      ) : (
        <AppChip label={DAILY_CHARGES_TEXT.PENDING} variant="warning" />
      ),
    elapsed_seconds: (row) => (
      <span className={!row.settled_at ? 'daily-control-pending' : ''}>
        {elapsedLabel(row.elapsed_seconds)}
        {!row.settled_at ? DAILY_CHARGES_TEXT.UNSETTLED_SUFFIX : ''}
      </span>
    ),
    amount: (row) => formatMoney(row.amount || 0)
  }
  const columns = DAILY_CHARGES_COLUMNS.map((column) => ({
    ...column,
    render: columnRenderers[column.key]
  }))
  return (
    <div className="daily-control-detail" aria-label={DAILY_CHARGES_TEXT.ARIA_LABEL}>
      <DetailDataTable
        ariaLabel={DAILY_CHARGES_TEXT.TABLE_ARIA_LABEL}
        caption={
          user && !loading ? (
            <div className="daily-control-detail-summary">
              {DAILY_CHARGES_TEXT.SUMMARY(
                user.charges_count - user.pending_charges_count,
                user.charges_count,
                formatMoney(user.collected_amount || 0)
              )}
            </div>
          ) : null
        }
        columns={columns}
        rows={rows}
        rowKey={DAILY_CHARGES_CONFIG.ROW_KEY}
        loading={loading}
        minWidth={DAILY_CHARGES_CONFIG.MIN_WIDTH}
        emptyContent={DAILY_CHARGES_TEXT.EMPTY}
      />
    </div>
  )
}
